import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { CircularProgress, Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";

import { store, useAppSelector } from '@/app/store';
import { RoleContext } from '@/app/api/common';

//import { LanguageDropDown } from '@/components/Languages/Languages';
import FlexRow from '@/components/Common/FlexRow';
import ConnectionStatus from '@/components/Common/ConnectionStatus';
import ChangeNotificationBanner from '@/components/Common/ChangeNotificationBanner';
import SessionExpiringModal from '@/components/Common/SessionExpiringModal';
import EmailBanner from '@/components/Common/EmailBanner';
import Sidebar from '@/components/Sidebar/Sidebar';

import {
  ChangeEmailModal,
} from '@/pages/Account/EmailModals';
import ChangePassword from '@/pages/Profile/ChangePassword';

import Routing from '@/routes/Routing';

const sidebarRoutes: string[] = ["projects", "channel_lists", "channels", "users", "settings/apps", "settings/epg", "settings/languages", "settings/tags", "settings/statistics", "settings/firmware", "settings/clonesettings", "audit","settings/trackformats", "settings/mailer", "settings/monitoring", "settings/system"];

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logins = useAppSelector((state) => state.logins);
  const [opened, setOpened] = React.useState(false);
  const [alwaysOpen, setAlwaysOpen]  = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Email-flow modals. Mutually exclusive — opening one closes the others.
  const [showChangeEmail, setShowChangeEmail] = React.useState(false);
  const [showChangePassword, setShowChangePassword] = React.useState(false);
  
  const ctx = React.useMemo(() => ({
    admin:			logins.login &&
    				logins.login.role == 'admin',
    role:			logins.login
    				?	logins.login.role
    				:	"",
    privilages:logins.login?.privileges
  }), [logins]);
  
  const pathname = useLocation().pathname;
  const pathArr = pathname.split("/").filter((path) => path != '');

  const localProjectId = logins.login?.local_project_id;
  const seededLanding = React.useRef(false);
  React.useEffect(() => {
    if (!localProjectId || seededLanding.current) return;
    seededLanding.current = true;
    navigate(`/projects/${encodeURIComponent(localProjectId)}`, { replace: true });
  }, [localProjectId, navigate]);
  
  let location = "projects";
  for (const route of sidebarRoutes) {
    const routeArr = route.split("/");
    if (pathArr.length < routeArr.length)
      continue;
    
    let found = true;
    for (let p = 0; p < routeArr.length; p++) {
      if (pathArr[p] != routeArr[p]) {
        found = false;
        break;
      }
    }
    
    if (found) {
      location = route;
      break;
    }
  }
  
  const handleResize = React.useCallback((entries: any) => {
    const ao =	entries[0].target.offsetWidth > 920;
    
    /*console.log(
      "Home/handleResize"+
      "/ao=" + ao +
      "/alwaysOpen=" + alwaysOpen +
      "/entries=", entries
    );*/
    
    setAlwaysOpen(ao);
  }, [alwaysOpen]);
  
  React.useEffect(() => {
    if (	!containerRef ||
          	!containerRef.current
       )
      return;
    
    const observer = new ResizeObserver(handleResize);
    observer.observe(containerRef.current);
    
    return () => {
      console.log("Home/useEffect/onDestroy!");
      observer.disconnect();
    };
  }, []);
  
  return (
  <DndProvider backend={HTML5Backend}>
  <RoleContext.Provider value={ctx}>
    <FlexRow direction="row"
             className="fixed top-0 left-0 right-0 bg-[--Primetime-main] h-[64px] px-[16px] py-[8px] z-50">
        <FlexRow>
        {!alwaysOpen &&
          <div className="cursor-pointer mr-[15px]"
               onClick={() => setOpened(!opened)}>
            <span className="material-icons !text-[--Lines]">
              menu
            </span>
          </div>
        }
          <img src="/assets/img/logo.svg" />
        </FlexRow>
        <FlexRow flex={0} className="gap-[8px]">
          <ChangeNotificationBanner />
          {/*<ConnectionStatus />*/}
          {/*<LanguageDropDown />*/}
          <Popover key={1}
                   placement="bottom-end"
                   classNames={{
                     content: "rounded-none",
                   }}>
            <PopoverTrigger>
              <div>
                <span className="material-icons cursor-pointer !text-[--Lines] !text-[32px]">
                  account_circle
                </span>
              </div>
            </PopoverTrigger>
            <PopoverContent>
              <FlexRow flex={0}
                       direction="column"
                       className="bg-white text-black min-w-[205px] px-[8px] py-[8px]">
                <FlexRow className="p-[8px] gap-[8px]"
                         direction="column"
                         align="start">
                  <div className="font-bold m14">
                    { t('login:user-role', {	user:	logins.login
                    									?	logins.login.username
                    									:	'',
                    							role:	logins.login
                    									?	t('login:roles:' + logins.login.role)
                    									:	'',
                    					   }
                      )
                    }
                  </div>
                  <div className="r12">
                    {	logins.login &&
                    	logins.login.email
                    	?	logins.login.email
                    	:	"-"
                    }
                  </div>
                </FlexRow>
                <Divider />
                <FlexRow className="p-[8px] gap-[8px]"
                         direction="column"
                         align="start">
                  { logins.supports2FA &&
                  <FlexRow className="r12 gap-[8px] cursor-pointer"
                           onClick={() => setShowChangeEmail(true)}>
                    <span className="material-icons !text-black text-[16px]">
                      mail
                    </span>
                    <span>{ t('email:menu:change') }</span>
                  </FlexRow>
                  }
                  <FlexRow className="r12 gap-[8px] cursor-pointer"
                           onClick={() => setShowChangePassword(true)}>
                    <span className="material-icons !text-black text-[16px]">
                      lock_reset
                    </span>
                    <span>{ t('email:menu:change_password') }</span>
                  </FlexRow>
                  { logins.supports2FA &&
                  <FlexRow className="r12 gap-[8px] cursor-pointer"
                           onClick={() => { setOpened(false); navigate('/settings/two-factor'); }}>
                    <span className="material-icons !text-black text-[16px]">
                      security
                    </span>
                    <span>{ t('email:menu:two_factor') }</span>
                  </FlexRow>
                  }
                  <FlexRow className="cursor-pointer gap-[8px]"
                       onClick={() => store.performLogout()}>
                    <span className="material-icons !text-[--Primetime-main] text-[16px]">
                      logout
                    </span>
                    <span className="r12 text-[--Primetime-main]">
                      { t('header:signout') }
                    </span>
                  </FlexRow>
                </FlexRow>
              </FlexRow>
            </PopoverContent>
          </Popover>
        </FlexRow>
    </FlexRow>
    <Sidebar opened={opened || alwaysOpen}
             location={location} />
    <FlexRow innerRef={containerRef}
             direction="column"
             align="start"
             className={[
               'pt-[64px] transition-padding-left duration-500 transform',
               opened || alwaysOpen ? 'pl-[282px]' : 'pl-[0px]',
             ]}>
      <EmailBanner />
      <Routing />
    </FlexRow>
    {showChangeEmail &&
      <ChangeEmailModal onClose={() => setShowChangeEmail(false)} />
    }
    {showChangePassword &&
      <ChangePassword
        onClose={() => setShowChangePassword(false)} />
    }
    <SessionExpiringModal />
  </RoleContext.Provider>
  </DndProvider>
  
  );
};

export default Home;
